import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  DeforestationCategory,
  DegradationDriverType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { DeforestationExplainer } from '@/components/NCExplainers/DeforestationExplainer';
import { COMPLIANCE_LOGOS_DEFORESTATION } from '@/config/constants';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { DeforestationGraphTile } from './GraphTile';
import { DeforestationCardStatsTile } from './StatsTile';

export const Deforestation = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.deforestation.labels.deforestation')}>
      <DeforestationCard />
    </PlotCardWrapper>
  );
};

const DeforestationCard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: IndicatorName.deforestation_total_historic_graph,
    benchmark: IndicatorName.deforestation_total_benchmark_graph,
    latestAnalysis: IndicatorName.deforestation_total,
    oneYearPctChange: IndicatorName.deforestation_previous_1y_pct_change,
    oneYearUplift: IndicatorName.deforestation_total_previous_1y_uplift,
    drivers: IndicatorName.deforestation_driver_percentage,
  };
  const plot = usePlot().data;
  const facts = usePlotFacts(Object.values(factNames));

  const isBiotara = ['70fd9c45-2ae1-4cb6-a99b-41767901cf17'].includes(plot.organization.id);

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestFact = getFactFromFactArray<number>(facts, factNames.latestAnalysis, {
    [PlotReportFactLabelNameEnum.deforestation]: DeforestationCategory.forest_loss,
  });
  const oneYearPctChangeFact = getFactFromFactArray<number>(facts, factNames.oneYearPctChange);
  const oneYearUpliftFact = getFactFromFactArray<number>(facts, factNames.oneYearUplift);
  const benchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark);

  const drivers: PlotReportFactElement[] | undefined = getFactsFromFactArray<number>(facts, factNames.drivers);
  const dominantDriver = drivers?.find((driver: PlotReportFactElement) =>
    driver.labels?.some(
      (label) =>
        label.name === PlotReportFactLabelNameEnum.deforestation_driver_type &&
        label.value === DegradationDriverType.dominant,
    ),
  );
  const secondaryDriver = drivers?.find((driver: PlotReportFactElement) =>
    driver.labels?.some(
      (label) =>
        label.name === PlotReportFactLabelNameEnum.deforestation_driver_type &&
        label.value === DegradationDriverType.secondary,
    ),
  );

  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestFact?.value) || !isNullish(oneYearPctChangeFact?.value) || !isNullish(oneYearUpliftFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<DeforestationExplainer />} data-testid='deforestation-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.deforestation.labels.deforestation')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <DeforestationGraphTile graphData={graphData} benchmarkData={benchmarkFact?.value} />
        <DeforestationCardStatsTile
          latestAnalysisFact={latestFact}
          oneYearPctChangeFact={oneYearPctChangeFact}
          oneYearUpliftFact={oneYearUpliftFact}
          benchmarkFact={benchmarkFact}
          dominantDriver={isBiotara ? dominantDriver : undefined}
          secondaryDriver={isBiotara ? secondaryDriver : undefined}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_DEFORESTATION} />
    </NCCard>
  );
};
